import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  Alert,
  Switch,
  FormGroup,
  FormControlLabel,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { SecuritySettings, getSecuritySettings, updateSecuritySettings } from '../../../../firebase/services/securityService';

const SecuritySettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Security settings state
  const [settings, setSettings] = useState<SecuritySettings>({
    sessionTimeouts: {
      admin: 120,
      staff: 60,
      customer: 30
    },
    twoFactorEnabled: false,
    passcodeSettings: {
      length: 6,
      requireNumbers: true,
      requireLetters: false,
      expireDays: 30,
    }
  });
  
  // Add state for addRoleValue
  const [addRoleValue, setAddRoleValue] = useState('');
  
  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const loadedSettings = await getSecuritySettings();
        setSettings(loadedSettings);
      } catch (err) {
        setError('Failed to load security settings');
        console.error('Error loading security settings:', err);
      }
    };
    
    loadSettings();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await updateSecuritySettings(settings);
      setSuccess('Security settings updated successfully');
    } catch (err) {
      setError('Failed to update security settings');
      console.error('Error updating security settings:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Security Settings
      </Typography>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Authentication Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <FormGroup>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={settings.twoFactorEnabled} 
                        onChange={(e) => setSettings({...settings, twoFactorEnabled: e.target.checked})} 
                      />
                    } 
                    label="Enable Two-Factor Authentication" 
                  />
                </FormGroup>
                
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Session Timeout by Role
                </Typography>
                {Object.entries(settings.sessionTimeouts).map(([role, timeout]) => (
                  <Box key={role} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      label={`Role: ${role}`}
                      type="number"
                      value={timeout}
                      onChange={e => {
                        const value = parseInt(e.target.value) || 0;
                        setSettings(s => ({
                          ...s,
                          sessionTimeouts: { ...s.sessionTimeouts, [role]: value }
                        }));
                      }}
                      sx={{ mr: 2, flex: 1 }}
                      inputProps={{ min: 0, max: 1440 }}
                      helperText="Minutes (0 = never)"
                    />
                    {role !== 'admin' && (
                      <Button
                        color="error"
                        onClick={() => {
                          const { [role]: _, ...rest } = settings.sessionTimeouts;
                          setSettings(s => ({ ...s, sessionTimeouts: rest }));
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <TextField
                    label="Add Role"
                    size="small"
                    value={addRoleValue || ''}
                    onChange={e => setAddRoleValue(e.target.value)}
                    sx={{ mr: 2, flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (addRoleValue && !(addRoleValue in settings.sessionTimeouts)) {
                        setSettings(s => ({
                          ...s,
                          sessionTimeouts: { ...s.sessionTimeouts, [addRoleValue]: 60 }
                        }));
                        setAddRoleValue('');
                      }
                    }}
                    disabled={!addRoleValue || addRoleValue in settings.sessionTimeouts}
                  >
                    Add Role
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Passcode Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TextField
                  label="Passcode Length"
                  type="number"
                  value={settings.passcodeSettings.length}
                  onChange={(e) => setSettings({
                    ...settings,
                    passcodeSettings: {
                      ...settings.passcodeSettings,
                      length: parseInt(e.target.value) || 6
                    }
                  })}
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 4, max: 12 }}
                />
                
                <FormGroup>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={settings.passcodeSettings.requireNumbers} 
                        onChange={(e) => setSettings({
                          ...settings,
                          passcodeSettings: {
                            ...settings.passcodeSettings,
                            requireNumbers: e.target.checked
                          }
                        })} 
                      />
                    } 
                    label="Require Numbers" 
                  />
                  
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={settings.passcodeSettings.requireLetters} 
                        onChange={(e) => setSettings({
                          ...settings,
                          passcodeSettings: {
                            ...settings.passcodeSettings,
                            requireLetters: e.target.checked
                          }
                        })} 
                      />
                    } 
                    label="Require Letters" 
                  />
                </FormGroup>
                
                <TextField
                  label="Expiration (days)"
                  type="number"
                  value={settings.passcodeSettings.expireDays}
                  onChange={(e) => setSettings({
                    ...settings,
                    passcodeSettings: {
                      ...settings.passcodeSettings,
                      expireDays: parseInt(e.target.value) || 0
                    }
                  })}
                  fullWidth
                  margin="normal"
                  helperText="How often passcodes must be changed (0 = never)"
                  inputProps={{ min: 0, max: 365 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                type="submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default SecuritySettingsPage; 